import { QueryClient, QueryFunction } from "@tanstack/react-query";

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new ApiError(res.status, `${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options: Pick<RequestInit, "signal"> = {},
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    signal: options.signal,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
// Note: Query keys must be declared as arrays for getQueryFn's join logic to work correctly
// e.g., queryKey: ['/api/users', userId] not queryKey: [`/api/users/${userId}`]
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export interface AnalyticsQueryParams {
  days?: number;
  contentType?: string;
}

export function buildAnalyticsQueryKey(
  endpoint: string,
  params: AnalyticsQueryParams = {}
): readonly [string, ...unknown[]] {
  const parts: unknown[] = [endpoint];
  if (params.days !== undefined) parts.push(params.days);
  if (params.contentType !== undefined && params.contentType !== "all") parts.push(params.contentType);
  return parts as unknown as readonly [string, ...unknown[]];
}

export function analyticsQueryFn<T>(
  endpoint: string,
  params: AnalyticsQueryParams = {}
): () => Promise<T> {
  return async () => {
    const searchParams = new URLSearchParams();
    if (params.days !== undefined) {
      searchParams.set("days", params.days.toString());
    }
    if (params.contentType !== undefined && params.contentType !== "all") {
      searchParams.set("contentType", params.contentType);
    }
    
    const queryString = searchParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    const res = await fetch(url, {
      credentials: "include",
    });
    
    await throwIfResNotOk(res);
    return await res.json();
  };
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for reuse
      retry: false,
      networkMode: "offlineFirst", // Return cached data immediately, refetch in background
    },
    mutations: {
      retry: false,
    },
  },
});
